import { createFileRoute, Navigate } from '@tanstack/react-router';
import {
  Loader2,
  LogIn,
  Mail,
  MessageSquare,
  Shield,
  Sparkles,
  Tag,
  Users,
} from 'lucide-react';
import { useMe, AuthError, goToLogin } from '@/lib/auth';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/')({
  component: Home,
});

function Home() {
  const { data: me, isLoading, error } = useMe();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" strokeWidth={1.75} />
          <span className="text-sm">Signing you in…</span>
        </div>
      </div>
    );
  }

  if (error instanceof AuthError) return <LoginLanding />;
  if (!me) return null;

  // Authenticated users land in the inbox — settings live at /settings.
  return <Navigate to="/inbox" />;
}

// --- Branded login landing ----------------------------------------------

function LoginLanding() {
  const features: { icon: typeof Mail; title: string; body: string }[] = [
    {
      icon: Mail,
      title: 'All your Gmail in one place',
      body: 'Connect personal and shared inboxes. Threads sync continuously.',
    },
    {
      icon: Users,
      title: 'Built for teams',
      body: 'Comment internally, assign threads, share access per teammate.',
    },
    {
      icon: MessageSquare,
      title: 'Reply without leaving',
      body: 'Drafts auto-save, peers see you typing, undo every action.',
    },
    {
      icon: Sparkles,
      title: 'AI that knows your clients',
      body: 'Thread summaries with CRM context. Your model, your key.',
    },
    {
      icon: Tag,
      title: 'Organise at speed',
      body: 'Tags, snooze, rules, bulk actions, swipe to archive on mobile.',
    },
    {
      icon: Shield,
      title: 'Encrypted at rest',
      body: 'Subjects, bodies, comments — all encrypted per workspace.',
    },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="grid w-full max-w-5xl gap-10 md:grid-cols-2 md:gap-16">
          <section className="flex flex-col justify-center">
            <img
              src="/icon-512.png"
              alt="Wharf Mail"
              width={56}
              height={56}
              className="size-14 rounded-2xl shadow-sm"
            />
            <h1 className="mt-6 text-3xl font-semibold tracking-tight md:text-4xl">
              Welcome to Wharf Mail
            </h1>
            <p className="mt-3 max-w-md text-sm text-muted-foreground md:text-base">
              The collaborative inbox for Pressific and Tech Generation. Sign in with
              your Clougency account — same SSO as the rest of the suite.
            </p>
            <div className="mt-6 flex flex-col items-start gap-2">
              <Button onClick={goToLogin} size="lg" className="h-11 gap-2 px-5 text-sm">
                <LogIn className="size-4" strokeWidth={2} />
                Sign in with Clougency
              </Button>
              <p className="text-[11px] text-muted-foreground">
                Your inbox is encrypted at rest. We never sell or share its contents.
              </p>
            </div>
          </section>

          <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {features.map(({ icon: Icon, title, body }) => (
              <div key={title} className="flex flex-col gap-2 rounded-lg border bg-card/50 p-4">
                <div className="flex size-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Icon className="size-4" strokeWidth={1.75} />
                </div>
                <div className="text-sm font-semibold leading-tight">{title}</div>
                <div className="text-xs leading-snug text-muted-foreground">{body}</div>
              </div>
            ))}
          </section>
        </div>
      </div>
      <footer className="border-t bg-card/30 px-4 py-3 text-center text-[11px] text-muted-foreground">
        Wharf Mail · A Pressific × Tech Generation product · Hosted by Clougency
      </footer>
    </div>
  );
}
